import { sound } from '@pixi/sound';
import { Container, InteractionEvent, Loader, Sprite } from 'pixi.js';
import { ForthCutScene } from '.';
import { logging, LOG_EVENT, LOG_SCENE } from '../api/log';
import { Player, Scaffolding } from '../class/jumping';
import { LOCALE_DATA } from '../constant';
import { Game, intersectionLL, Scene, Store, TextureFactory } from '../core';
import { IEventEmitter, Line, Point, Updatable } from '../interface';
import { DrawingGameUI, VanishText } from '../ui';
import JumpingGameProgress from '../ui/JumpingGameProgress';
import { secondsFormatting, secondsToScore } from '../util';
import { loadGoogleAdvertisement, showGoogleAdvertisement } from '../util/AD';
import MainScene from './MainScene';

class HeartItem extends Container {
  private sprite: Sprite;
  public used: boolean = false;

  constructor() {
    super();
    const texture = TextureFactory.instance.getSingleTexture('/textures/objects/etc/heart.png');

    this.sprite = new Sprite(texture);
    this.sprite.anchor.set(0.5);

    this.addChild(this.sprite);
  }

  public use(): void {
    this.used = true;
  }

  public update(elapsed: number): void {
    if (this.used && this.alpha >= 0.01) {
      this.scale.x += (3 - this.scale.x) * 0.1 * elapsed;
      this.scale.y = this.scale.x;
      this.alpha += -this.alpha * 0.1 * elapsed;
    } else if (this.used) {
      this.visible = false;
    } else if (!this.used && this.alpha <= 0.99) {
      this.visible = true;
      this.scale.x += (1 - this.scale.x) * 0.1 * elapsed;
      this.scale.y = this.scale.x;
      this.alpha += (1 - this.alpha) * 0.1 * elapsed;
    } else if (!this.used) {
      this.alpha = 1;
    }
  }
}

class BreadItem extends Container {
  private sprite: Sprite;
  public used: boolean = false;

  constructor() {
    super();
    this.sprite = new Sprite(TextureFactory.instance.getSingleTexture('/textures/objects/etc/bread.png'));
    this.sprite.anchor.set(0.5);

    this.addChild(this.sprite);
  }

  public use(): void {
    this.used = true;
  }

  public update(elapsed: number): void {
    if (this.used && this.alpha >= 0.01) {
      this.scale.x += (3 - this.scale.x) * 0.1 * elapsed;
      this.scale.y = this.scale.x;
      this.alpha += -this.alpha * 0.1 * elapsed;
    } else if (this.used) {
      this.visible = false;
    } else if (!this.used && this.alpha <= 0.99) {
      this.visible = true;
      this.scale.x += (1 - this.scale.x) * 0.1 * elapsed;
      this.scale.y = this.scale.x;
      this.alpha += (1 - this.alpha) * 0.1 * elapsed;
    } else if (!this.used) {
      this.alpha = 1;
    }
  }
}

class SuperJumpItem extends Container {
  private sprite: Sprite;
  private scaffolding?: Scaffolding;
  public disabled: boolean = false;

  constructor() {
    super();
    const texture = TextureFactory.instance.getSingleTexture('/textures/objects/etc/spring.png');

    this.sprite = new Sprite(texture);
    this.sprite.anchor.set(0.5, 0.95);
    this.sprite.scale.set(1.2);

    this.addChild(this.sprite);
  }

  public update(elapsed: number): void {
    if (this.scaffolding) {
      this.x = this.scaffolding.position.x + this.scaffolding.width / 2;
      this.y = this.scaffolding.position.y;
    }

    if (this.disabled) {
      this.visible = false;
    } else {
      this.visible = true;
    }
  }

  public setTargetScaffolding(scaffolding: Scaffolding): void {
    this.scaffolding = scaffolding;
    this.disabled = false;
  }
}
class JumpingGameScene extends Scene {
  private targetWidth: number;
  private objects: Array<Updatable & IEventEmitter>;
  private scaffoldings: Array<Scaffolding>;
  private lastLandedScaffolding: Scaffolding;
  private backgrounds: Array<Sprite>;
  private ui: DrawingGameUI;
  private progress: JumpingGameProgress;
  public lastScaffoldingY: number = 0;
  private isBgmPlaying: boolean = false;
  public player: Player;
  public heartItem: HeartItem;
  public breadItem: BreadItem;
  public superJumpItem: SuperJumpItem;
  private lastPosition: number = 0;
  private getBread: boolean = false;

  constructor(game: Game) {
    super(game);

    this.objects = [];
    this.backgrounds = [];
    this.scaffoldings = [];
    this.targetWidth = 1080;

    this.progress = new JumpingGameProgress();
    this.container.addChild(this.progress);

    this.ui = new DrawingGameUI(this);
    this.ui.on('home', () => {
      const button = document.getElementById('sharebtn') as HTMLElement;
      button.style.display = 'none';
      this.game.changeScene(MainScene);

      logging(
        LOG_EVENT.GO_TO_HOME,
        LOG_SCENE.JUMPING_GAME_SCENE,
        Store.instance.data.playername,
        window.navigator.userAgent
      );
    });
    this.ui.on('pause', () => {
      this.game.stop();
    });
    this.ui.on('start', () => {
      this.game.start();
    });

    this.ui.on('ad', async () => {
      try {
        const isLoaded: boolean = await loadGoogleAdvertisement();
        if (isLoaded) {
          await showGoogleAdvertisement();
          logging(
            LOG_EVENT.AD,
            LOG_SCENE.JUMPING_GAME_SCENE,
            Store.instance.data.playername,
            window.navigator.userAgent
          );
        }
      } catch (e) {
        console.log(e);
      }
      if (Store.instance.state.hp < 3) {
        Store.instance.state.hp = 3;
      }
      const button = document.getElementById('sharebtn') as HTMLElement;
      button.style.display = 'none';
      this.game.changeScene(JumpingGameScene);

      logging(
        LOG_EVENT.REPLAY,
        LOG_SCENE.JUMPING_GAME_SCENE,
        Store.instance.data.playername,
        window.navigator.userAgent
      );
    });
    this.container.addChild(this.ui);

    for (let i = 1; i <= 3; i++) {
      const sprite = new Sprite(TextureFactory.instance.getSingleTexture('/textures/bg/bg_jumping.png'));
      sprite.x = 0;
      sprite.y = 500 - i * 1920;
      this.backgrounds.push(sprite);
      this.stage.addChild(sprite);
    }

    const lastBg = new Sprite(TextureFactory.instance.getSingleTexture('/textures/bg/bg_jumping_last.png'));
    lastBg.x = 0;
    this.stage.addChild(lastBg);

    this.generateScaffolding({ x: 0, y: 0 }, false, true, true);
    this.lastLandedScaffolding = this.scaffoldings[0];

    // Super Jump Item
    this.superJumpItem = new SuperJumpItem();
    this.stage.addChild(this.superJumpItem);

    const length = 120; // 120
    for (let i = 0; i < length; i++) {
      if (i === length - 1) {
        this.generateScaffolding({ x: 0, y: (i + 1) * -180 }, false, false, true, true);
        this.lastScaffoldingY = (i + 1) * -180 + 10;
      } else {
        const width = this.targetWidth / 5 + (Math.random() * this.targetWidth) / 3;
        // const isStatic = i < 70 || Math.random() < 0.3;
        const isStatic = Math.random() < 0.5;
        this.generateScaffolding(
          { x: Math.random() * (this.targetWidth - width), y: (i + 1) * -180 },
          i % 2 === 0 ? (Math.random() < 0.5 ? true : false) : false,
          false,
          isStatic
        );
      }
    }
    lastBg.y = this.scaffoldings[this.scaffoldings.length - 1].position.y - 630;

    this.superJumpItem.setTargetScaffolding(this.scaffoldings[6]);

    this.player = new Player();
    this.player.position.x = this.targetWidth / 2;
    this.player.position.y = 0;
    this.player.on('DIE', (): void => {
      this.gameOver();
    });
    this.player.on('hit', (damage: number) => {
      const scoreText: VanishText = new VanishText(`HP -${damage}`, { fill: 0xff5555 });
      scoreText.x = this.player.position.x;
      scoreText.y = this.player.position.y - 90;
      scoreText.on('destroy', () => {
        this.objects.splice(this.objects.indexOf(scoreText), 1);
        this.stage.removeChild(scoreText);
        scoreText.destroy();
      });
      this.objects.push(scoreText);
      this.stage.addChild(scoreText);
    });
    this.objects.push(this.player);
    this.stage.addChild(this.player.container);

    this.camera.positionChasingCoefficient = 0.15;
    this.camera.setBoundary({
      min: { x: 0, y: lastBg.y },
      max: { x: this.targetWidth, y: 550 },
    });
    this.camera.zoomChasingCoefficient = 0.1;
    this.camera.setOffset({ x: 0, y: 0 });
    this.camera.setTargetPosition(this.player.position, true);

    // Heart Item
    this.heartItem = new HeartItem();
    this.heartItem.x = 540;
    this.heartItem.y = -1000;
    this.stage.addChild(this.heartItem);

    // Bread Item
    this.breadItem = new BreadItem();
    this.stage.addChild(this.breadItem);

    if (!Store.instance.data.costumes[2]) {
      this.breadItem.x = 540;
      this.breadItem.y = -5000;
    } else {
      this.breadItem.x = Infinity;
      this.breadItem.y = Infinity;
    }

    this.on('shared', () => {
      this.ui.notificationNinimoDT = 120;

      logging(
        LOG_EVENT.COPY_SHARE,
        LOG_SCENE.JUMPING_GAME_SCENE,
        Store.instance.data.playername,
        window.navigator.userAgent
      );
    });

    logging(
      LOG_EVENT.GAME_START,
      LOG_SCENE.JUMPING_GAME_SCENE,
      Store.instance.data.playername,
      window.navigator.userAgent
    );
  }

  private gameOver(): void {
    logging(
      LOG_EVENT.GAME_OVER,
      LOG_SCENE.JUMPING_GAME_SCENE,
      Store.instance.data.playername,
      window.navigator.userAgent
    );
    this.ui.showTitle('Game Over');
    setTimeout(() => {
      this.ui.showGameOver(`3`, this.player.score.toString());
    }, 2000);
  }

  private gameClear(): void {
    logging(
      LOG_EVENT.GAME_CLEAR,
      LOG_SCENE.JUMPING_GAME_SCENE,
      Store.instance.data.playername,
      window.navigator.userAgent
    );
    this.ui.showTitle('Clear!');
    this.player.trigger('CLEAR');
    this.player.update(1);

    this.isRunning = false;
    setTimeout(() => {
      const totalSeconds = Math.floor(this.duration / 1000);
      this.ui.showTitle(`${secondsFormatting(totalSeconds)}`);

      setTimeout(() => {
        sound.play('/audio/bonus.mp3', { loop: false, volume: Store.instance.state.volume as number });
        const score = secondsToScore(totalSeconds);
        this.player.score += score;
        this.ui.showTitle(`+${score} Score`);

        setTimeout(() => {
          if (this.getBread) {
            if (!Store.instance.data.costumes[2]) {
              Store.instance.data.costumes[2] = true;
              Store.instance.save();
              this.ui.showGetItemPopup(2);
              this.ui.once('POPUP_END', () => {
                if (!Store.instance.data.costumes[3]) {
                  Store.instance.data.costumes[3] = true;
                  Store.instance.save();
                  this.ui.showGetItemPopup(3);
                  this.ui.once('POPUP_END', () => {
                    Store.instance.state.hp = this.player.hp;
                    Store.instance.state.score = this.player.score;
                    this.game.changeScene(ForthCutScene);
                  });
                } else {
                  Store.instance.state.hp = this.player.hp;
                  Store.instance.state.score = this.player.score;
                  this.game.changeScene(ForthCutScene);
                }
              });
            }
          } else {
            if (!Store.instance.data.costumes[3]) {
              Store.instance.data.costumes[3] = true;
              Store.instance.save();
              this.ui.showGetItemPopup(3);
              this.ui.once('POPUP_END', () => {
                Store.instance.state.hp = this.player.hp;
                Store.instance.state.score = this.player.score;
                this.game.changeScene(ForthCutScene);
              });
            } else {
              Store.instance.state.hp = this.player.hp;
              Store.instance.state.score = this.player.score;
              this.game.changeScene(ForthCutScene);
            }
          }
        }, 2500);
      }, 2500);
    }, 2500);
  }

  public generateScaffolding(
    position: Point,
    brokenable: boolean,
    isBottom: boolean = false,
    isStatic: boolean = false,
    isLast: boolean = false
  ): void {
    const scaffolding = new Scaffolding({
      position,
      brokenable,
      range: [0, this.targetWidth],
      static: isStatic,
      isBottom,
      isLast,
    });

    this.objects.push(scaffolding);
    this.scaffoldings.push(scaffolding);

    this.stage.addChild(scaffolding.container);
  }

  public teleport(): void {
    const targetY = this.player.position.y - 1000;

    for (const scaffolding of this.scaffoldings) {
      if (scaffolding.position.y < targetY && !scaffolding.brokenable) {
        this.player.position.y = scaffolding.position.y - 10;
        this.player.position.x = scaffolding.position.x + scaffolding.width / 2;
        this.lastLandedScaffolding = scaffolding;
        if (
          this.player.position.y <= -1000 &&
          this.camera.boundary.max.y > this.player.position.y + this.game.height / 2 / this.camera.zoom
        ) {
          this.camera.boundary.max.y = this.player.position.y + this.game.height / 2 / this.camera.zoom;
        }
        break;
      }
    }
  }

  public superJump(): void {
    sound.play('/audio/spring.mp3', { loop: false, volume: Store.instance.state.volume as number });
    this.player.velocity.y = -60;
    this.player.isSuperJump = true;
  }

  public update(elapsed: number): void {
    super.update(elapsed);

    this.camera.setOffset({
      x: 0,
      y: this.player.velocity.y * 10,
    });

    // 속도
    this.player.velocity.y += 0.6 * elapsed;
    if (this.player.velocity.x > 0.1 * elapsed) {
      this.player.velocity.x -= 0.1 * elapsed;
    } else if (this.player.velocity.x < -0.1 * elapsed) {
      this.player.velocity.x += 0.1 * elapsed;
    } else {
      this.player.velocity.x = 0;
    }

    if (
      this.lastPosition < Math.floor(-this.player.position.y / 180) &&
      Math.floor(this.player.position.x / 180) <= 200
    ) {
      const score = 4;

      this.lastPosition = Math.floor(-this.player.position.y / 180);
      this.player.score += score;

      const scoreText: VanishText = new VanishText(`+${score}`, { fill: 0xefa500 });
      scoreText.x = this.player.position.x;
      scoreText.y = this.player.position.y - 90;
      scoreText.on('destroy', () => {
        this.objects.splice(this.objects.indexOf(scoreText), 1);
        this.stage.removeChild(scoreText);
        scoreText.destroy();
      });
      this.objects.push(scoreText);
      this.stage.addChild(scoreText);
    }

    // 벽면 튕김
    if (this.player.position.x + this.player.velocity.x < 0) {
      this.player.position.x = 0;

      if (this.player.velocity.x < 0) {
        this.player.velocity.x *= -1;
      }
    } else if (this.player.position.x + this.player.velocity.x > this.targetWidth) {
      this.player.position.x = this.targetWidth;

      if (this.player.velocity.x > 0) {
        this.player.velocity.x *= -1;
      }
    }

    const trajectory: Line = [
      { x: this.player.position.x, y: this.player.position.y },
      {
        x: this.player.position.x + this.player.velocity.x * elapsed,
        y: this.player.position.y + this.player.velocity.y * elapsed,
      },
    ];
    if (this.player.velocity.y > 0) {
      for (let i = this.scaffoldings.length - 1; i >= 0; i--) {
        const scaffolding = this.scaffoldings[i];

        const dt = intersectionLL(trajectory, [
          { x: scaffolding.position.x, y: scaffolding.position.y },
          { x: scaffolding.position.x + scaffolding.width, y: scaffolding.position.y },
        ]);

        if (dt >= 0 && dt <= 1) {
          if (scaffolding.brokenable) {
            scaffolding.vanish();
          } else {
            this.player.velocity.y = 0;
            this.player.velocity.x = scaffolding.velocity.x;
            this.player.position.y = scaffolding.position.y;

            if (this.player.state !== 'IDLE' && this.player.state !== 'CLEAR') {
              this.player.isSuperJump = false;
              this.player.trigger(`LANDING`);
              this.lastLandedScaffolding = scaffolding;
              if (!this.isRunning) {
                this.isRunning = true;
                this.isStarted = true;
              }
              if (this.player.position.y < this.lastScaffoldingY) {
                this.gameClear();
              }

              if (
                this.player.position.y <= -1000 &&
                this.camera.boundary.max.y > this.camera.position.y + this.game.height / 2 / this.camera.zoom
              ) {
                this.camera.boundary.max.y = this.camera.position.y + this.game.height / 2 / this.camera.zoom;
              }

              if (this.heartItem.y > this.camera.boundary.max.y) {
                this.heartItem.y -= 10000;
                this.heartItem.used = false;
              }

              if (this.superJumpItem.y > this.camera.boundary.max.y) {
                for (const scaffolding of this.scaffoldings) {
                  if (
                    scaffolding.position.y < this.player.position.y - 3500 &&
                    !scaffolding.brokenable &&
                    !scaffolding.isLast
                  ) {
                    this.superJumpItem.setTargetScaffolding(scaffolding);
                    break;
                  }
                }
              }
            }
            break;
          }
        }
      }
    }

    for (const object of this.objects) {
      object.update(elapsed);
    }

    let maxY = -Infinity;
    let minY = Infinity;
    for (const point of this.camera.viewport) {
      if (maxY < point.y) {
        maxY = point.y;
      }
      if (minY > point.y) {
        minY = point.y;
      }
    }

    if (this.player.position.y <= this.scaffoldings[this.scaffoldings.length - 1].position.y) {
      this.scaffoldings[this.scaffoldings.length - 1].container.zIndex = Infinity;
    }

    for (const background of this.backgrounds) {
      if (background.y > maxY) {
        background.y -= 1920 * this.backgrounds.length;
      }
      if (background.y + background.height < minY) {
        background.y += 1920 * this.backgrounds.length;
      }
    }

    if (this.player.position.y > maxY) {
      sound.play('/audio/crash.mp3', { loop: false, volume: Store.instance.state.volume as number });
      sound.play('/audio/heartlose.mp3', { loop: false, volume: Store.instance.state.volume as number });
      this.player.position.y = this.lastLandedScaffolding.position.y - 10;
      this.player.position.x = this.lastLandedScaffolding.position.x + this.lastLandedScaffolding.width / 2;
      this.player.hit(1);
    }

    if (this.lastLandedScaffolding.position.y === 0) {
      if (!this.ui.showNotificationText) {
        this.ui.showNotification(LOCALE_DATA[Store.instance.state.locale].JUMPING_TUTORIAL);
      }
    } else if (this.ui.showNotificationText) {
      this.ui.hideNotification();
    }

    this.ui.setHP(this.player.hp);
    this.ui.setScore(this.player.score);
    this.ui.update(elapsed);

    this.progress.update(this.game.width, this.game.height, this.player.position.y / this.lastScaffoldingY);

    const heartItemDiff = Math.sqrt(
      (this.player.position.x - this.heartItem.x) ** 2 + (this.player.position.y - this.heartItem.y) ** 2
    );
    if (!this.heartItem.used && heartItemDiff < 130) {
      sound.play('/audio/getting_item.mp3', { loop: false, volume: Store.instance.state.volume as number });
      sound.play('/audio/heartget.mp3', { loop: false, volume: Store.instance.state.volume as number });
      if (this.player.hp < 5) {
        this.player.hp += 1;
      }
      this.heartItem.use();

      const healthText: VanishText = new VanishText(`HP +1`, { fill: 0x66cc66 });
      healthText.x = this.player.position.x;
      healthText.y = this.player.position.y - 90;
      healthText.on('destroy', () => {
        this.objects.splice(this.objects.indexOf(healthText), 1);
        this.stage.removeChild(healthText);
        healthText.destroy();
      });
      this.objects.push(healthText);
      this.stage.addChild(healthText);
    }
    this.heartItem.update(elapsed);

    const breadItemDiff = Math.sqrt(
      (this.player.position.x - this.breadItem.x) ** 2 + (this.player.position.y - this.breadItem.y) ** 2
    );
    if (!this.breadItem.used && breadItemDiff < 130) {
      sound.play('/audio/getting_item.mp3', { loop: false, volume: Store.instance.state.volume as number });
      this.breadItem.use();
      this.getBread = true;
    }
    this.breadItem.update(elapsed);

    const superJumpItemDiff = Math.sqrt(
      (this.player.position.x - this.superJumpItem.x) ** 2 + (this.player.position.y - this.superJumpItem.y) ** 2
    );
    if (this.player.velocity.y > 0 && superJumpItemDiff < 130 && !this.superJumpItem.disabled) {
      this.superJump();
      this.superJumpItem.disabled = true;
      const score = 50;

      this.player.score += score;

      const scoreText: VanishText = new VanishText(`+${score}`, { fill: 0xefa500 });
      scoreText.x = this.player.position.x;
      scoreText.y = this.player.position.y - 90;
      scoreText.on('destroy', () => {
        this.objects.splice(this.objects.indexOf(scoreText), 1);
        this.stage.removeChild(scoreText);
        scoreText.destroy();
      });
      this.objects.push(scoreText);
      this.stage.addChild(scoreText);
    }
    this.superJumpItem.update(elapsed);
  }

  public static preload = async (callback: (currentValue: number, maxValue: number) => void): Promise<void> => {
    const assets: Array<string> = [
      '/audio/bgm_stage_3.mp3',
      '/audio/crash.mp3',
      '/audio/heartlose.mp3',
      '/audio/heartget.mp3',
      '/audio/getting_item.mp3',
      '/audio/jump.mp3',
      '/audio/spring.mp3',
      '/audio/pung.mp3',

      '/textures/objects/etc/bread.png',
      '/textures/objects/etc/scaffolding_1.png',
      '/textures/objects/etc/scaffolding_2.png',
      '/textures/objects/etc/scaffolding_3.png',
      '/textures/objects/etc/scaffolding_4.png',
      '/textures/objects/etc/scaffolding_5.png',
      '/textures/objects/etc/scaffolding_6.png',
      '/textures/objects/etc/scaffolding_7.png',
      '/textures/objects/etc/scaffolding_8.png',
      '/textures/objects/etc/scaffolding_9.png',
      '/textures/objects/etc/heart.png',
      '/textures/objects/etc/spring.png',
      '/textures/objects/ninimo/jump.webp',
      '/textures/bg/bg_jumping.png',
      '/textures/bg/bg_jumping_last.png',
    ];

    let maxLoaded = 1;
    for (const asset of assets) {
      if (!Loader.shared.resources[asset]) {
        Loader.shared.add(asset, asset);
        maxLoaded++;
      }
    }

    let loaded: number = 1;
    return new Promise((resolve) => {
      Loader.shared.onLoad.add(() => {
        callback(++loaded, maxLoaded);
      });
      Loader.shared.onComplete.once(() => {
        resolve();
      });
      Loader.shared.load();
    });
  };

  public destroy(): void {
    super.destroy();
  }

  protected pointerup = (event: InteractionEvent) => {
    if (this.game.paused) return;
    if (this.player.canJump) {
      this.player.next();
    }
  };

  public onBegin(): void {
    if (!this.isBgmPlaying) {
      sound.play('/audio/bgm_stage_3.mp3', { loop: true, volume: Store.instance.state.volume as number });
      this.isBgmPlaying = true;
    }
  }
}

export default JumpingGameScene;
